import { keepPreviousData, useMutation } from '@tanstack/react-query'
import head from 'lodash/head'

import useDraft from '@shared/hooks/src/useDraft'
import useQuery from '@shared/hooks/src/useQuery'
import API from '@shared/services/src/API'
import { QK } from '@shared/utils'

import useRecentOrderWarning, { RECENT_ORDER_DAYS } from '@hooks/useRecentOrderWarning'
import { usePatientLabs } from '@pages/Labs/PatientLabs'

export const useLabOrderDraft = (patientId, encounterId) => {
  if (patientId && encounterId) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useDraft(`lab_order_${patientId}_${encounterId}`, { reason: '', labs: {} })
  }
  return {
    draft: undefined,
    setDraft: () => undefined,
    removeDraft: () => undefined,
  }
}

export function useLabOrder(patientId) {
  return useMutation({
    mutationFn: (data) => API.patients.id(patientId).labs.order(data),
  })
}

/**
 * Warns the provider when the patient already has a lab ordered within the last 7 days.
 *
 * @param {Object} patient - The patient the labs will be associated with
 * @param {Object} options
 * @param {Function} options.onConfirm - Called when the provider acknowledges the warning
 * @param {Function} options.onCancel - Called when the provider decides not to order another lab
 */
export function useRecentLabOrderWarning(patient, { onConfirm, onCancel }) {
  const { data: lab, isPending } = usePatientLabs(
    patient?.id,
    { limit: 1, offset: 0, order: 'desc', sort: 'created_at' },
    { enabled: Boolean(patient?.id), select: head }
  )

  useRecentOrderWarning({
    isPending,
    createdAt: lab?.createdAt,
    title: 'Recent Lab Order Found',
    description: `A lab order was created for this patient within the last ${RECENT_ORDER_DAYS} days. Please review the patient's Lab history before continuing.`,
    onConfirm,
    onCancel,
  })
}

export function useLabVendors(patientId, encounterId, reason, options = {}) {
  const query = { encounter_id: encounterId, lab_reason: reason }

  return useQuery({
    queryKey: QK.patients.id(patientId).labs.vendors.list(query),
    queryFn: () => API.patients.id(patientId).labs.vendors(query),
    enabled: Boolean(patientId) && Boolean(encounterId) && Boolean(reason),
    placeholderData: keepPreviousData,
    ...options,
  })
}
